/**
 * Language preference — stored per user on the server (users.language), cached
 * in localStorage so the very first paint after a reload is already correct.
 *
 * Boot order matters: src/i18n.js reads the cached value while it constructs the
 * VueI18n instance, so by the time anything here runs the locale is already
 * applied to i18n. This module owns everything *around* that — document
 * direction, Vuetify's rtl flag, and writing the choice back to the user record.
 */
import i18n, {
  SUPPORTED_LOCALES,
  RTL_LOCALES,
  LOCALE_STORAGE_KEY,
  normalizeLocale
} from '@/i18n'
import vuetify from '@/plugins/vuetify'

export { SUPPORTED_LOCALES, normalizeLocale }

export const LANGUAGE_OPTIONS = [
  { locale: 'en', apiValue: 'EN', nativeLabel: 'English' },
  { locale: 'ar', apiValue: 'AR', nativeLabel: 'العربية' }
]

export function isRtl (locale) {
  return RTL_LOCALES.indexOf(normalizeLocale(locale)) > -1
}

/** 'en' -> 'EN'. The User model stores the language uppercased. */
export function toApiLanguage (locale) {
  return normalizeLocale(locale).toUpperCase()
}

export function getLocale () {
  return normalizeLocale(i18n.locale)
}

export function getCachedLocale () {
  try {
    return normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY))
  } catch (e) {
    return 'en'
  }
}

function cacheLocale (locale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch (e) {
    /* private mode / storage disabled — the server copy is still authoritative */
  }
}

/**
 * Point i18n, Vuetify and the document at a locale. Idempotent, and safe to
 * call before the root component mounts.
 */
export function applyLocale (locale) {
  const loc = normalizeLocale(locale)
  const rtl = isRtl(loc)

  i18n.locale = loc
  if (vuetify && vuetify.framework) {
    vuetify.framework.rtl = rtl
    // Vuetify's own strings (data-table footer, empty state, pagination,
    // calendar day names) come from its `lang` table, not from src/locales.
    if (vuetify.framework.lang) vuetify.framework.lang.current = loc
  }

  if (typeof document !== 'undefined') {
    const html = document.documentElement
    html.setAttribute('lang', loc)
    html.setAttribute('dir', rtl ? 'rtl' : 'ltr')
    html.classList.toggle('app-rtl', rtl)
    html.classList.toggle('app-ltr', !rtl)
  }
  return loc
}

/** Bootstrap from the cached value. Called once from main.js before mount. */
export function initLocale () {
  return applyLocale(getCachedLocale())
}

/**
 * Adopt the language held on a user record — called right after sign-in, so the
 * preference follows the user onto any browser they log into.
 */
export function adoptUserLocale (user) {
  const loc = normalizeLocale(user && user.language)
  cacheLocale(loc)
  return applyLocale(loc)
}

const SAVE_WARNING_KEY = 'DONE_LANGUAGE_SAVE_WARNING'

function flagSaveWarning () {
  try { sessionStorage.setItem(SAVE_WARNING_KEY, '1') } catch (e) { /* ignore */ }
}
function clearSaveWarning () {
  try { sessionStorage.removeItem(SAVE_WARNING_KEY) } catch (e) { /* ignore */ }
}

/**
 * True once, if the last language switch could not be written to the user
 * record. Reading it clears the flag, so the warning is shown a single time
 * after the reload that the switch triggered.
 */
export function consumeSaveWarning () {
  try {
    const had = sessionStorage.getItem(SAVE_WARNING_KEY) === '1'
    if (had) clearSaveWarning()
    return had
  } catch (e) {
    return false
  }
}

function saveToServer (apiLanguage) {
  // required lazily so that merely importing this module doesn't drag the whole
  // Apollo client into the boot path
  const { mutation } = require('@/core/methods/mutation.js')
  return mutation({ language: apiLanguage }, 'user/api/updateUserLanguage')
}

function updateCachedUser (apiLanguage) {
  try {
    const raw = localStorage.getItem('DONE_USER')
    if (!raw) return
    const user = JSON.parse(raw)
    user.language = apiLanguage
    localStorage.setItem('DONE_USER', JSON.stringify(user))
  } catch (e) {
    /* non-fatal: the next sign-in refreshes this */
  }
}

/**
 * Change the signed-in user's language.
 *
 * Reloads by default. Thousands of labels in this codebase are captured in
 * data() (Vuetify `headers` arrays being the big one) and so are evaluated once
 * per component instance; a reload is the one cheap way to guarantee every
 * one of them is rebuilt in the new language. Language switching is a rare,
 * deliberate action, so the cost is acceptable — and it is far safer than
 * converting several hundred data() blocks into computeds.
 *
 * @returns {Promise<{saved: boolean, error?: Error}>} whether the server
 *          accepted the preference. The local switch happens either way.
 */
export async function setUserLanguage (locale, options) {
  const opts = options || {}
  const reload = opts.reload !== false
  const loc = normalizeLocale(locale)
  const apiLanguage = toApiLanguage(loc)

  cacheLocale(loc)
  applyLocale(loc)

  let result = { saved: false }
  try {
    await saveToServer(apiLanguage)
    updateCachedUser(apiLanguage)
    result.saved = true
    clearSaveWarning()
  } catch (error) {
    // The user still gets the language they asked for on this browser; it just
    // won't follow them elsewhere until a later switch succeeds. Flag it so the
    // warning survives the reload below and can be shown on the way back up.
    // eslint-disable-next-line no-console
    console.warn('[language] could not persist preference to the user record', error)
    flagSaveWarning()
    result = { saved: false, error }
  }

  if (reload) window.location.reload()
  return result
}
